import { create } from "zustand";
import { rangeDistanceKm, resolveOdoKm as resolveOdoKmShared } from "../../pages/reports/utils/intervalDistance";
import {
  buildWindows,
  loadDeviceHistory,
  processInBatches,
  toNumber,
  DEVICE_CONCURRENCY,
} from "../../pages/reports/utils/trackingHistoryWindows";

const DAY_MS = 24 * 60 * 60 * 1000;

export const ODOMETER_COLORS = [
  "#2563EB",
  "#DB2777",
  "#16A34A",
  "#D97706",
  "#7C3AED",
  "#0891B2",
  "#DC2626",
  "#65A30D",
];

export interface OdometerResult {
  deviceId: string;
  vehicleName: string;
  color: string;
  kmInicial: number | null;
  kmFinal: number | null;
  kmRecorrido: number | null;
  firstTs: number | null;
  lastTs: number | null;
  loading: boolean;
  error: string | null;
}

export interface OdometerDevice {
  deviceId: string;
  vehicleName: string;
}

interface ReportOdometerState {
  results: Record<string, OdometerResult>;
  order: string[];
  loading: boolean;
  from: string | null;
  to: string | null;
  tz: string | null;
  diffDays: number;
}

interface ReportOdometerActions {
  loadRange: (
    devices: OdometerDevice[],
    from: string,
    to: string,
    opts?: { tz?: string },
  ) => Promise<void>;
  clear: () => void;
}

type ReportOdometerStore = ReportOdometerState & ReportOdometerActions;

export const useReportOdometerStore = create<ReportOdometerStore>()((set) => ({
  results: {},
  order: [],
  loading: false,
  from: null,
  to: null,
  tz: null,
  diffDays: 0,

  loadRange: async (devices, from, to, opts) => {
    const resolvedTz = opts?.tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const order = devices.map((d) => d.deviceId);

    const fromMs = new Date(from).getTime();
    const toMs = new Date(to).getTime();
    const windows = buildWindows(fromMs, toMs);
    const diffDays = Math.ceil(Math.abs(toMs - fromMs) / DAY_MS) || 1;

    const initial: Record<string, OdometerResult> = {};
    devices.forEach((d, i) => {
      initial[d.deviceId] = {
        deviceId: d.deviceId,
        vehicleName: d.vehicleName,
        color: ODOMETER_COLORS[i % ODOMETER_COLORS.length],
        kmInicial: null,
        kmFinal: null,
        kmRecorrido: null,
        firstTs: null,
        lastTs: null,
        loading: true,
        error: null,
      };
    });

    set({ results: initial, order, loading: true, from, to, tz: resolvedTz, diffDays });

    await processInBatches(devices, DEVICE_CONCURRENCY, async (d) => {
      const { points, failedChunks } = await loadDeviceHistory(
        d.deviceId,
        windows,
        resolvedTz,
      );

      const odoPoints = points
        .map((p) => ({ ts: p.ts, odoKm: toNumber(p.params?.odo_km) }))
        .filter((p) => p.odoKm != null) as Array<{ ts: number; odoKm: number }>;

      // Lo recorrido sale del odómetro limpio (reseteos y basura fuera) y del GPS
      // donde no hay odómetro, igual que el archivo; restar lecturas pasaba un
      // reseteo entero (GL 31, 15-oct: 944 km contra 857 reales).
      const recorrido = (() => {
        const pts = [...points]
          .filter((p) => p.ts != null)
          .sort((a, b) => a.ts - b.ts)
          .map((p) => ({ ts: p.ts, lat: p.lat ?? null, lon: p.lng ?? null, speed: p.speed ?? null, odoKm: resolveOdoKmShared(p.params as Record<string, unknown>) }));
        return pts.length ? rangeDistanceKm(pts, pts[0].ts) : null;
      })();

      set((state) => {
        const current = state.results[d.deviceId];
        if (!current) return state;

        const first = odoPoints[0] ?? null;
        const last = odoPoints[odoPoints.length - 1] ?? null;

        return {
          results: {
            ...state.results,
            [d.deviceId]: {
              ...current,
              kmInicial: first ? first.odoKm : null,
              kmFinal: last ? last.odoKm : null,
              kmRecorrido: first && last ? recorrido : null,
              firstTs: first ? first.ts : null,
              lastTs: last ? last.ts : null,
              loading: false,
              error:
                odoPoints.length === 0 && failedChunks > 0
                  ? "No se pudo obtener el odómetro (servicio no disponible)."
                  : odoPoints.length === 0
                    ? "Sin datos de odómetro en el rango."
                    : failedChunks > 0
                      ? `${failedChunks} tramo(s) con error`
                      : null,
            },
          },
        };
      });
    });

    set({ loading: false });
  },

  clear: () =>
    set({
      results: {},
      order: [],
      loading: false,
      from: null,
      to: null,
      tz: null,
      diffDays: 0,
    }),
}));
